import {
    CopyOutlined,
    UploadOutlined,
    CheckCircleFilled,
} from '@ant-design/icons';
import {
    Button,
    Divider,
    Input,
    Modal,
    Select,
    Spin,
    Typography,
    Upload,
    Tabs,
    Tag,
    Radio,
} from 'antd';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
    createMinimaxVideo,
    createRunwayVideo,
    getNextRunwayAccount,
    uploadMinimaxFile,
    uploadRunwayFile,
    getNextHailuoAccount,
} from '../services/api';
import { resizeAndConvertToBase64 } from '../utils/imageHelper';
const { TabPane } = Tabs;
const { Title } = Typography;

const cinematicPresets = [
    {
        label: 'Debut',
        value: 'Truck left,Push in,Pan right',
        video: '/video/compressed_debut.mp4',
    },
    {
        label: 'Freedom',
        value: 'Push out,Pedestal up,Tilt down',
        video: '/video/compressed_freedom.mp4',
    },
    {
        label: 'Right circling',
        value: 'Truck right,Pan left,Tracking shot',
        video: '/video/rightcircling_360w_optimized_2.mp4',
    },
    {
        label: 'Left circling',
        value: 'Truck left,Pan right,Tracking shot',
        video: '/video/leftcircling_360w_optimized_2.mp4',
    },
    {
        label: 'Upward tilt',
        value: 'Push in,Pedestal up',
        video: '/video/upwardtilt_360w_optimized_2.mp4',
    },
    {
        label: 'Left walking',
        value: 'Truck left,Tracking shot',
        video: '/video/leftwalking_360w_optimized_2.mp4',
    },
    {
        label: 'Right walking',
        value: 'Truck right,Tracking shot',
        video: '/video/rightwalking_360w_optimized_2.mp4',
    },
    {
        label: 'Downward tilt',
        value: 'Pedestal down,Tilt up',
        video: '/video/downwardtilt_360w_optimized_2.mp4',
    },
    {
        label: 'Stage left',
        value: 'Pan left,Zoom in',
        video: '/video/stageleft_360w_optimized_2.mp4',
    },
    {
        label: 'Stage right',
        value: 'Pan right,Zoom in',
        video: '/video/stageright_360w_optimized_2.mp4',
    },
    {
        label: 'Scenic shot',
        value: 'Truck left,Pedestal up',
        video: '/video/scenicshot_360w_optimized_2.mp4',
    },
];
const freeMotionOptions = [
    {
        groupLabel: 'Truck',
        options: [
            {
                label: 'Truck left',
                video: '/video/truckleft_360w_optimized_2.mp4',
            },
            {
                label: 'Truck right',
                video: '/video/truckright_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Pan',
        options: [
            { label: 'Pan left', video: '/video/panleft_360w_optimized_2.mp4' },
            {
                label: 'Pan right',
                video: '/video/panright_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Push/Pull',
        options: [
            { label: 'Push in', video: '/video/pushin_360w_optimized_2.mp4' },
            { label: 'Pull out', video: '/video/pullout_360w_optimized_2.mp4' },
        ],
    },
    {
        groupLabel: 'Pedestal',
        options: [
            {
                label: 'Pedestal up',
                video: '/video/pedestalup_360w_optimized_2.mp4',
            },
            {
                label: 'Pedestal down',
                video: '/video/pedestaldown_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Tilt',
        options: [
            { label: 'Tilt up', video: '/video/tiltup_360w_optimized_2.mp4' },
            {
                label: 'Tilt down',
                video: '/video/tiltdown_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Zoom',
        options: [
            { label: 'Zoom in', video: '/video/zoomin_360w_optimized_2.mp4' },
            { label: 'Zoom out', video: '/video/zoomout_360w_optimized_2.mp4' },
        ],
    },
    {
        groupLabel: 'Shake',
        options: [
            { label: 'Shake', video: '/video/shake_360w_optimized_2.mp4' },
        ],
    },
    {
        groupLabel: 'Tracking shot',
        options: [
            {
                label: 'Tracking shot',
                video: '/video/trackingshot_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Static shot',
        options: [
            {
                label: 'Static shot',
                video: '/video/staticshot_360w_optimized_2.mp4',
            },
        ],
    },
];

const MotionGroupCard = ({
    group,
    selectedMotions,
    setSelectedMotions,
    setDescription,
    setCameraMovement,
}) => {
    const [hoveredVideo, setHoveredVideo] = useState(group.options[0].video);

    return (
        <div
            style={{
                border: '1px solid #ccc',
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: '0.3s',
            }}
        >
            <video
                src={hoveredVideo}
                style={{ width: '100%', height: 100, objectFit: 'cover' }}
                muted
                loop
                playsInline
                autoPlay
                disablePictureInPicture
                controls={false}
            />
            <div style={{ display: 'flex' }}>
                {group.options.map((motion) => {
                    const isSelected = selectedMotions.includes(motion.label);
                    return (
                        <Button
                            key={motion.label}
                            type={isSelected ? 'primary' : 'default'}
                            onClick={() => {
                                const groupMotionLabels = group.options.map(
                                    (opt) => opt.label,
                                );
                                let updatedMotions = selectedMotions.filter(
                                    (m) => !groupMotionLabels.includes(m),
                                );

                                const isAlreadySelected =
                                    selectedMotions.includes(motion.label);
                                if (!isAlreadySelected) {
                                    if (updatedMotions.length >= 3) {
                                        toast.error(
                                            'Chỉ được chọn tối đa 3 chuyển động',
                                        );
                                        return;
                                    }
                                    updatedMotions.push(motion.label);
                                }

                                setSelectedMotions(updatedMotions);

                                setDescription((prevDesc) => {
                                    const matches =
                                        prevDesc.match(/\[[^\]]*\]/g) || [];
                                    let cinematic = '';
                                    let free = '';

                                    if (
                                        matches.length > 0 &&
                                        cinematicPresets.some(
                                            (preset) =>
                                                preset.value ===
                                                matches[0].slice(1, -1).trim(),
                                        )
                                    ) {
                                        cinematic = matches[0];
                                    }
                                    const otherText = prevDesc
                                        .replace(/\[[^\]]*\]/g, '')
                                        .trim();

                                    free =
                                        updatedMotions.length > 0
                                            ? `[${updatedMotions.join(', ')}]`
                                            : '';

                                    return [otherText, cinematic, free]
                                        .filter(Boolean)
                                        .join(' ')
                                        .trim();
                                });

                                setCameraMovement(updatedMotions.join(', '));
                            }}
                            onMouseEnter={() => setHoveredVideo(motion.video)}
                            onMouseLeave={() =>
                                setHoveredVideo(group.options[0].video)
                            }
                            style={{ flex: 1, borderRadius: 0 }}
                        >
                            {motion.label}
                        </Button>
                    );
                })}
            </div>
        </div>
    );
};

const InputSection = ({
    provider,
    description,
    setDescription,
    title,
    onReload,
    setPreviews,
    minimaxMode,
    setMinimaxMode,
    image,
}) => {
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [aspectRatio, setAspectRatio] = useState('16:9');
    const [seconds, setSeconds] = useState(10);
    const [cameraModalVisible, setCameraModalVisible] = useState(false);
    const [cameraMovement, setCameraMovement] = useState('');
    const [selectedMotions, setSelectedMotions] = useState([]);

    useEffect(() => {
        if (image?.fileId && image?.thumbnail) {
            setFileList([
                {
                    uid: image.fileId,
                    name: 'Ảnh đã upload',
                    status: 'done',
                    url: image.thumbnail,
                },
            ]);
        }
    }, [JSON.stringify(image)]);

    const handleCreateVideo = async () => {
        if (
            provider === 'minimax' &&
            minimaxMode === 'image' &&
            !fileList.length
        ) {
            toast.error('Vui lòng chọn ít nhất 1 ảnh');
            return;
        }

        const finalPrompt = description.trim();
        const accountWeb = JSON.parse(localStorage.getItem('account_web'));
        const ownerId = accountWeb?.id;

        if (!ownerId) {
            toast.error('Không tìm thấy tài khoản đăng nhập');
            return;
        }

        setLoading(true);
        try {
            if (provider === 'minimax') {
                if (minimaxMode === 'text') {
                    const selectedAcc = await getNextHailuoAccount(ownerId);
                    const accountId = selectedAcc.accountId;

                    await createMinimaxVideo(
                        accountId,
                        null,
                        finalPrompt,
                        title,
                        ownerId,
                        null,
                        'T2V-01-Director',
                        null,
                        'text',
                    );
                    toast.success('Đã gửi yêu cầu tạo video từ văn bản!');
                } else {
                    for (const fileWrapper of fileList) {
                        let fileId, accountId, accEmail, imageBase64;

                        // Kiểm tra nếu file đã có fileId (từ server)
                        if (
                            fileWrapper.uid &&
                            fileWrapper.url &&
                            !fileWrapper.originFileObj
                        ) {
                            // File đã upload trước đó, dùng lại fileId
                            fileId = fileWrapper.uid;
                            imageBase64 = fileWrapper.url; // Dùng thumbnail làm imageBase64

                            // Lấy account mới
                            const selectedAcc = await getNextHailuoAccount(
                                ownerId,
                            );
                            accountId = selectedAcc.accountId;
                            accEmail = selectedAcc.email;
                        } else {
                            // File mới, cần upload
                            const fileObj =
                                fileWrapper.originFileObj || fileWrapper;

                            const uploadResult = await uploadMinimaxFile(
                                fileObj,
                                ownerId,
                            );
                            fileId = uploadResult.fileId;
                            accountId = uploadResult.accountId;
                            accEmail = uploadResult.email;

                            imageBase64 = await resizeAndConvertToBase64(
                                fileObj,
                            );
                        }

                        const videoData = await createMinimaxVideo(
                            accountId,
                            fileId,
                            finalPrompt,
                            title,
                            ownerId,
                            accEmail,
                            'I2V-01-Director',
                            imageBase64,
                            'image',
                        );

                        const videoId = String(videoData.videoId).match(
                            /(?:video:)?(\d+)$/,
                        )?.[1];

                        setPreviews((prev) => [
                            ...prev,
                            {
                                videoId,
                                tempImageURL: imageBase64,
                            },
                        ]);
                    }

                    toast.success(`Đã tạo ${fileList.length} video với Hailuo`);
                }
            } else if (provider === 'runway') {
                const account = await getNextRunwayAccount(ownerId);
                const email = account.email;

                for (const fileWrapper of fileList) {
                    const fileObj = fileWrapper.originFileObj || fileWrapper;
                    const assetId = await uploadRunwayFile(email, fileObj);

                    await createRunwayVideo(
                        email,
                        assetId,
                        finalPrompt,
                        title,
                        ownerId,
                        aspectRatio,
                        seconds,
                    );
                }

                toast.success(`Đã tạo ${fileList.length} video với Runway`);
            } else {
                toast.error('Provider không hợp lệ');
            }

            if (onReload) onReload();
        } catch (err) {
            if (err.message?.includes('only 0 can be generated')) {
                toast.error(
                    '⛔ Tài khoản AI hiện tại đang xử lý video khác. Vui lòng thử lại sau hoặc dùng tài khoản khác.',
                );
            } else if (err.message?.includes('Too Many Requests')) {
                toast.error(
                    '🚫 Server đang quá tải. Vui lòng thử lại sau ít phút.',
                );
            } else if (err.message?.includes('Insufficient credits')) {
                toast.error('⚠️ Tài khoản đã hết lượt tạo video.');
            } else {
                toast.error(err.message || '❌ Có lỗi khi tạo video');
            }
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        beforeUpload: (file) => {
            const allowedTypes = [
                'image/png',
                'image/jpeg',
                'image/jpg',
                'image/gif',
            ];

            if (provider === 'minimax' && !allowedTypes.includes(file.type)) {
                toast.error(
                    'Định dạng ảnh không được hỗ trợ. Chỉ chấp nhận PNG, JPEG, JPG, GIF',
                );
                return Upload.LIST_IGNORE;
            }

            const img = new Image();
            const objectUrl = URL.createObjectURL(file);
            img.src = objectUrl;

            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                if (img.width < 512 || img.height < 512) {
                    toast(
                        'Ảnh có độ phân giải thấp (<512x512). Video có thể bị mờ hoặc lỗi.',
                        { icon: '⚠️' },
                    );
                }

                setFileList((prev) => [...prev, file]);
            };

            return false;
        },
        fileList,
        accept: 'image/png,image/jpeg,image/jpg,image/gif',
        multiple: true,
    };

    useEffect(() => {
        setFileList([]);
    }, [provider]);

    const [showSave, setShowSave] = useState(false);
    useEffect(() => {
        setShowSave(!!description?.trim());
    }, [description]);

    const handleCopyDescription = async () => {
        try {
            await navigator.clipboard.writeText(description);
            toast.success('Đã copy mô tả video!');
        } catch (err) {
            toast.error('Không thể copy mô tả');
        }
    };

    return (
        <Spin spinning={loading}>
            {!(provider === 'minimax' && minimaxMode === 'text') && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                        }}
                    >
                        <Title level={5} style={{ margin: 0 }}>
                            Mô tả video
                        </Title>

                        {provider === 'minimax' && (
                            <Button
                                size="small"
                                type="default"
                                onClick={() => {
                                    setCameraMovement('');
                                    setCameraModalVisible(true);
                                }}
                                style={{
                                    border: '1px solid #444',
                                    fontWeight: 300,
                                }}
                            >
                                Chọn góc quay
                            </Button>
                        )}
                    </div>

                    <Input.TextArea
                        rows={3}
                        placeholder="Nhập mô tả video..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        style={{
                            height: '170px',
                            fontSize: '16px',
                            padding: '12px',
                        }}
                    />
                    {showSave && (
                        <div style={{ marginTop: 8, textAlign: 'right' }}>
                            <Button
                                type="default"
                                shape="circle"
                                icon={<CopyOutlined />}
                                onClick={handleCopyDescription}
                                title="Copy mô tả video"
                            />
                        </div>
                    )}

                    {provider === 'runway' && (
                        <>
                            <Divider />
                            <Title level={5}>Tuỳ chọn video</Title>
                            <Select
                                style={{ width: '100%', marginBottom: 8 }}
                                value={aspectRatio}
                                onChange={setAspectRatio}
                                options={[
                                    {
                                        value: '16:9',
                                        label: '📺 16:9 Landscape',
                                    },
                                    {
                                        value: '9:16',
                                        label: '📱 9:16 Portrait',
                                    },
                                    { value: '1:1', label: '🔲 1:1 Vuông' },
                                    { value: '4:3', label: '🖼️ 4:3' },
                                    { value: '3:4', label: '🧱 3:4' },
                                    { value: '21:9', label: '🎬 21:9 Cinema' },
                                ]}
                                placeholder="Tỷ lệ khung hình"
                            />
                            <Select
                                style={{ width: '100%' }}
                                value={seconds}
                                onChange={setSeconds}
                                options={[
                                    { value: 5, label: '⏱️ 5 giây' },
                                    { value: 10, label: '⏱️ 10 giây' },
                                ]}
                                placeholder="Thời lượng video"
                            />
                        </>
                    )}

                    {/* Chỉ hiển thị upload nếu là Image mode */}
                    {provider === 'minimax' && minimaxMode === 'image' && (
                        <>
                            <Divider />
                            <Title level={5}>
                                <span style={{ color: 'red' }}>*</span> Upload
                                ảnh
                            </Title>
                            <Upload
                                {...uploadProps}
                                fileList={fileList}
                                onRemove={(file) => {
                                    setFileList((prev) =>
                                        prev.filter((f) => f.uid !== file.uid),
                                    );
                                }}
                            >
                                <Button icon={<UploadOutlined />}>
                                    Chọn ảnh
                                </Button>
                            </Upload>
                            <div
                                style={{
                                    marginTop: 16,
                                    display: 'flex',
                                    gap: 16,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {fileList.map((file, index) => {
                                    const previewURL =
                                        file.thumbUrl ||
                                        file.tempImageURL ||
                                        file.preview ||
                                        file.url ||
                                        (file instanceof File
                                            ? URL.createObjectURL(file)
                                            : null) ||
                                        (file.originFileObj
                                            ? URL.createObjectURL(
                                                  file.originFileObj,
                                              )
                                            : null);
                                    const fileName =
                                        file.name ||
                                        file.originFileObj?.name ||
                                        `Ảnh ${index + 1}` ||
                                        file.name;

                                    return (
                                        <div
                                            key={index}
                                            style={{
                                                position: 'relative',
                                                width: 110,
                                                textAlign: 'center',
                                                fontSize: 12,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    marginBottom: 4,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                title={fileName}
                                            >
                                                {fileName}
                                            </div>
                                            <img
                                                src={previewURL}
                                                alt={`uploaded-${index}`}
                                                style={{
                                                    width: 100,
                                                    height: 100,
                                                    objectFit: 'cover',
                                                    border: '1px solid #ccc',
                                                    borderRadius: 6,
                                                }}
                                            />
                                            <Button
                                                size="small"
                                                danger
                                                shape="circle"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    transform:
                                                        'translate(50%, -50%)',
                                                    zIndex: 2,
                                                }}
                                                onClick={() =>
                                                    setFileList((prev) =>
                                                        prev.filter(
                                                            (_, i) =>
                                                                i !== index,
                                                        ),
                                                    )
                                                }
                                            >
                                                ✕
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    <Divider />
                    <Button type="primary" block onClick={handleCreateVideo}>
                        Tạo Video
                    </Button>
                </>
            )}

            <Modal
                open={cameraModalVisible}
                onCancel={() => setCameraModalVisible(false)}
                footer={null}
                title="Chọn góc quay"
                width={800}
            >
                <Tabs defaultActiveKey="cinematic">
                    <TabPane tab=" Cinematic shots" key="cinematic">
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(160px, 1fr))',
                                gap: '16px',
                            }}
                        >
                            {cinematicPresets.map((item) => {
                                const motionFromDesc = description
                                    .match(/\[(.*?)\]/)?.[1]
                                    ?.trim();
                                const isSelected =
                                    motionFromDesc === item.value;

                                return (
                                    <div
                                        key={item.label}
                                        onClick={() => {
                                            const motion = item.value;
                                            const currentMatch =
                                                description.match(/\[(.*?)\]/);
                                            const currentMotion =
                                                currentMatch?.[1]?.trim();
                                            const isSame =
                                                currentMotion === motion;

                                            let newDescription = description;

                                            if (isSame) {
                                                newDescription = description
                                                    .replace(/\[(.*?)\]/g, '')
                                                    .trim();
                                                setCameraMovement('');
                                            } else {
                                                const cleaned = description
                                                    .replace(/\[(.*?)\]/g, '')
                                                    .trim();
                                                newDescription =
                                                    `${cleaned} [${motion}]`.trim();
                                                setCameraMovement(motion);
                                            }

                                            setDescription(newDescription);
                                            setCameraModalVisible(false);
                                        }}
                                        style={{
                                            position: 'relative',
                                            cursor: 'pointer',
                                            border: isSelected
                                                ? '2px solid #1677ff'
                                                : '1px solid #ccc',
                                            backgroundColor: isSelected
                                                ? '#e6f4ff'
                                                : 'transparent',
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            boxShadow: isSelected
                                                ? '0 0 8px 2px rgba(22, 119, 255, 0.3)'
                                                : '0 2px 8px rgba(0,0,0,0.1)',
                                            transform: isSelected
                                                ? 'scale(1.02)'
                                                : 'none',
                                            transition: 'all 0.3s ease-in-out',
                                        }}
                                    >
                                        {isSelected && (
                                            <CheckCircleFilled
                                                style={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    color: '#1677ff',
                                                    fontSize: 20,
                                                    zIndex: 2,
                                                }}
                                            />
                                        )}
                                        <video
                                            src={item.video}
                                            style={{
                                                width: '100%',
                                                height: 100,
                                                objectFit: 'cover',
                                            }}
                                            muted
                                            loop
                                            playsInline
                                            autoPlay
                                            disablePictureInPicture
                                            controls={false}
                                        />
                                        <div
                                            style={{
                                                padding: '8px',
                                                textAlign: 'center',
                                                fontWeight: 500,
                                                color: isSelected
                                                    ? '#1677ff'
                                                    : undefined,
                                            }}
                                        >
                                            {item.label}
                                            {isSelected && (
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Đã chọn
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </TabPane>

                    <TabPane tab=" Free selection" key="free">
                        <div style={{ marginBottom: 12 }}>
                            <strong>
                                Đã chọn ({selectedMotions.length}/3):{' '}
                            </strong>
                            {selectedMotions.map((motion, index) => (
                                <Tag
                                    key={index}
                                    closable
                                    onClose={() =>
                                        setSelectedMotions(
                                            selectedMotions.filter(
                                                (m) => m !== motion,
                                            ),
                                        )
                                    }
                                >
                                    {motion}
                                </Tag>
                            ))}
                        </div>

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(160px, 1fr))',
                                gap: 16,
                            }}
                        >
                            {freeMotionOptions.map((group, idx) => (
                                <MotionGroupCard
                                    key={idx}
                                    group={group}
                                    selectedMotions={selectedMotions}
                                    setSelectedMotions={setSelectedMotions}
                                    setDescription={setDescription}
                                    setCameraMovement={setCameraMovement}
                                />
                            ))}
                        </div>

                        <div
                            style={{ marginTop: 20, textAlign: 'right' }}
                        ></div>
                    </TabPane>
                </Tabs>
            </Modal>
        </Spin>
    );
};

export default InputSection;
